import { useData } from "../runtime/provider";
import LBox from "@components/LBox";
import LIcon from "@components/LIcon";
import LScreen from "@components/LScreen";
import LText from "@components/LText";
import SocialButton from "@components/SocialButton";
import {
  type BackgroundColor,
  fonts,
  hexToRGB,
  scaleFont,
  theme,
} from "@theme";
import config from "@utils/config";
import { getViewPort } from "@utils/web/config";
import { useNativeState } from "@expo/ui";
import * as WebBrowser from "expo-web-browser";
import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Modal,
  StyleSheet,
  Pressable,
  Platform,
  useWindowDimensions,
  View,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { KeyboardAvoidingView } from "@components/KeyboardAvoidingView";
import { trackOnb } from "@components/Onboarding/analytics/trackOnboarding";
import Animated, {
  Easing,
  FadeIn,
  FadeInDown,
  FadeInRight,
  FadeInUp,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from "react-native-reanimated";
import Eye from "@components/Eye";
import LPressable from "@components/LPressable";
import useLoginRegister from "@hooks/useLoginRegister";
import LoginCarousel from "@components/Onboarding/LoginCarousel";
import PreviewChannelSwitcher from "@components/Account/Settings/PreviewChannelSwitcher";
import { toast, Toaster } from "sonner-native";
import { useLocalSearchParams } from "expo-router";
import { useFarcasterContext } from "@hooks/useFarcasterContext";
import { capabilities } from "../capabilities";
import { mvs } from "@utils/scale";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { presentIntercomSupport } from "@utils/presentIntercomSupport";
import {
  BottomSheetSubtitle,
  BottomSheetSubmitButton,
  BottomSheetTopBarLayout,
} from "@utils/swmModal/components/BottomSheetPrimitives";
import AppModalSheet from "@utils/appModal/components/AppModalSheet";
import {
  type SheetAdapterRef,
  useBottomSheetManager,
} from "react-native-bottom-sheet-stack";
import LoginSheetTextInput, {
  type LoginSheetTextInputRef,
} from "@components/LoginSheetTextInput";
import { StableKeyboardInset } from "@components/StableKeyboardInset";
import { CodeStep } from "@components/EmailCode/CodeStep";
import type {
  CodeStepLook,
  CodeStepStatus,
} from "@components/EmailCode/CodeStep";
import {
  FieldBecomesCode,
  OVER_BOX,
  useFieldBecomesCode,
} from "@components/EmailCode/fieldBecomesCode";
import type { FieldLook } from "@components/EmailCode/fieldBecomesCode";
import { TRPCClientError } from "@trpc/client";
import { captureException } from "@utils/captureException";

type AuthParams = {
  prefilledEmail?: string;
  prefilledPassword?: string;
};

const EmailModal = ({
  visible,
  onClose,
  children,
}: {
  visible: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) => {
  const overlay = (
    <Pressable
      style={[StyleSheet.absoluteFill, styles.modalOverlay]}
      onPress={onClose}
      accessible={false}
    >
      {Platform.OS === "web" ? (
        <Pressable
          style={styles.modalContent}
          onPress={(e) => e.stopPropagation()}
          accessible={false}
        >
          {children}
        </Pressable>
      ) : (
        <KeyboardAvoidingView
          behavior="translate-with-padding"
          keyboardVerticalOffset={24}
          style={styles.modalKeyboardContainer}
        >
          <Pressable
            style={styles.modalContent}
            onPress={(e) => e.stopPropagation()}
            accessible={false}
          >
            {children}
          </Pressable>
        </KeyboardAvoidingView>
      )}
    </Pressable>
  );

  if (Platform.OS === "web") {
    return visible ? overlay : null;
  }

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <KeyboardAvoidingView
        behavior="padding"
        keyboardVerticalOffset={40}
        style={{ flex: 1 }}
      >
        {overlay}
      </KeyboardAvoidingView>
      {Platform.OS === "android" && <Toaster theme="light" />}
    </Modal>
  );
};

const SHIMMER_WIDTH = 160;
const AUTH_BUTTON_HEIGHT = Math.min(48, Math.max(44, mvs(48)));
const AUTH_BUTTON_FONT_SIZE = Math.min(18, scaleFont(18));
const AUTH_BUTTON_LINE_HEIGHT = Math.min(20, scaleFont(20));
const AUTH_BUTTON_ICON_SIZE = Math.min(20, scaleFont(20));
const TOP_BAR_HEIGHT = 40;
const LOGIN_TITLE_HEIGHT = scaleFont(40);
const LOGIN_TITLE_VERTICAL_PADDING = theme.spacing.m2 * 2;
const AUTH_BUTTON_GAP = theme.spacing.s4;
const AUTH_SEPARATOR_HEIGHT = scaleFont(20);
const SOCIAL_ICON_BUTTON_SIZE = 56;
const LEGAL_TEXT_HEIGHT = scaleFont(34);
const LEGAL_TEXT_GAP = theme.spacing.s3;
const LEGAL_TEXT_BOTTOM_PADDING = theme.spacing.m1;

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const sanitizeAuthInput = (text: string) => text.replace(/[^\x20-\x7E]/g, "");
const VALID_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (email: string) => VALID_EMAIL_REGEX.test(email.trim());

type LoginEmailSheetProps = {
  initialEmail?: string;
  onLoginSuccess?: () => void | Promise<void>;
};

const LoginEmailSheetContent = ({
  closeModal,
  initialEmail,
  onLoginSuccess,
}: LoginEmailSheetProps & { closeModal: () => void }) => {
  const initialCleanEmail = sanitizeAuthInput(initialEmail ?? "");
  const emailState = useNativeState(initialCleanEmail);
  const passwordState = useNativeState("");
  const [hasValidEmail, setHasValidEmail] = useState(
    isValidEmail(initialCleanEmail),
  );
  const [hasPassword, setHasPassword] = useState(false);
  const [hasMinPassword, setHasMinPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const passwordRef = useRef<LoginSheetTextInputRef>(null);
  const handleLoginSuccess = useCallback(async () => {
    await onLoginSuccess?.();
  }, [onLoginSuccess]);
  const { authenticate, isSubmitting, sendForgotEmail } = useLoginRegister(
    false,
    handleLoginSuccess,
  );

  const showPasswordButtonDisabled = !hasPassword || isSubmitting;
  const continueButtonEnabled =
    hasValidEmail && hasMinPassword && !isSubmitting;
  const inputPlaceholderColor = hexToRGB(theme.colors.black, 0.42);

  const handleEmailChange = useCallback((text: string) => {
    const cleanText = sanitizeAuthInput(text);
    setHasValidEmail(isValidEmail(cleanText));
  }, []);

  const handlePasswordChange = useCallback((text: string) => {
    setHasPassword(text.length > 0);
    setHasMinPassword(text.length >= 6);
  }, []);

  const handleContinueButton = useCallback(async () => {
    const email = sanitizeAuthInput(emailState.value).trim();
    const password = passwordState.value;
    closeModal();
    await authenticate(undefined, undefined, email, password);
  }, [authenticate, closeModal, emailState, passwordState]);

  return (
    <BottomSheetTopBarLayout
      title="Sign up or Log in"
      contentProps={{ gap: "s4" }}
    >
      <BottomSheetSubtitle>
        Use your email and password to continue with Lissen.
      </BottomSheetSubtitle>
      <LBox gap="s3" width="100%">
        <LBox style={styles.sheetInputFrame}>
          <LoginSheetTextInput
            placeholder="Email"
            testID="login-email-input"
            value={emailState}
            onChangeText={handleEmailChange}
            placeholderTextColor={inputPlaceholderColor}
            onSubmitEditing={() => passwordRef.current?.focus()}
            autoCapitalize="none"
            autoCorrect={false}
            autoComplete="email"
            keyboardType="email-address"
            returnKeyType="next"
            autoFocus
            editable={!isSubmitting}
            cursorColor={theme.colors.black}
            selectionColor={hexToRGB(theme.colors.black, 0.2)}
            hostStyle={styles.sheetInputHost}
            style={{
              opacity: isSubmitting ? 0.5 : 1,
            }}
            textStyle={styles.sheetInputText}
          />
        </LBox>
        <LBox justifyContent="center">
          <LBox style={styles.sheetInputFrame}>
            <LoginSheetTextInput
              placeholder="Password"
              testID="login-password-input"
              ref={passwordRef}
              value={passwordState}
              onChangeText={handlePasswordChange}
              autoCapitalize="none"
              autoCorrect={false}
              autoComplete="password"
              secureTextEntry={!showPassword}
              onSubmitEditing={handleContinueButton}
              returnKeyType="done"
              placeholderTextColor={inputPlaceholderColor}
              editable={!isSubmitting}
              cursorColor={theme.colors.black}
              selectionColor={hexToRGB(theme.colors.black, 0.2)}
              hostStyle={styles.sheetInputHost}
              style={{
                opacity: isSubmitting ? 0.5 : 1,
              }}
              textStyle={styles.sheetInputText}
            />
          </LBox>
          <Eye
            accessibilityLabel="Toggle password visibility"
            color={inputPlaceholderColor}
            testID="login-password-visibility"
            disabled={showPasswordButtonDisabled}
            showPassword={showPassword}
            onPress={() => setShowPassword((prev) => !prev)}
          />
        </LBox>
        <LPressable
          sentryEventName="forgot_password"
          accessibilityLabel="Forgot password?"
          testID="login-forgot-password"
          onPress={() =>
            sendForgotEmail(sanitizeAuthInput(emailState.value).trim())
          }
          disabled={!hasValidEmail || isSubmitting}
          style={{
            opacity: !hasValidEmail || isSubmitting ? 0.5 : 1,
          }}
        >
          <LText
            color="black"
            fontFamily={fonts.semiBold}
            fontSize={16}
            lineHeight={22}
            textAlign="center"
            paddingVertical="s2"
          >
            Forgot password?
          </LText>
        </LPressable>
        <BottomSheetSubmitButton
          label="CONTINUE"
          onPress={handleContinueButton}
          enabled={continueButtonEnabled}
          applyOpacityWhenDisabled={false}
          testID="SignUpButton"
          accessibilityLabel="CONTINUE"
          sentryEventName="login_continue"
        />
      </LBox>
    </BottomSheetTopBarLayout>
  );
};

const trpcErrorCode = (error: unknown) =>
  error instanceof TRPCClientError ? error.data?.code : undefined;

const SHEET_FIELD_FILL = hexToRGB(theme.colors.white, 0.38);
const SHEET_FIELD_BORDER = hexToRGB(theme.colors.white, 0.7);
const SHEET_FIELD_RADIUS = 22;
const SHEET_INPUT_TEXT_SIZE = 16;
const SHEET_INPUT_TEXT_LINE = 20;

// The code step on the login sheet's glass: the email field's own fill and
// outline, black for what is typed and pressed.
const CODE_STEP_LOOK: CodeStepLook = {
  text: theme.colors.black,
  secondary: hexToRGB(theme.colors.black, 0.62),
  surface: SHEET_FIELD_FILL,
  border: SHEET_FIELD_BORDER,
  filledBorder: hexToRGB(theme.colors.black, 0.35),
  focus: theme.colors.black,
  action: theme.colors.black,
  onAction: theme.colors.white,
  danger: "#a11616",
  success: "#15803d",
  successText: "#136c34",
  successFill: "rgba(21, 128, 61, 0.16)",
  chipFill: SHEET_FIELD_FILL,
  chipBorder: SHEET_FIELD_BORDER,
  slotHeight: 58,
  slotRadius: 16,
  slotGap: theme.spacing.s2,
  digitSize: 22,
  digitFont: fonts.semiBold,
  gap: theme.spacing.s4,
  fonts: { medium: fonts.medium, semiBold: fonts.semiBold },
};

const EMAIL_FIELD_LOOK: FieldLook = {
  surface: SHEET_FIELD_FILL,
  border: SHEET_FIELD_BORDER,
  borderClear: "rgba(255, 255, 255, 0)",
  radius: SHEET_FIELD_RADIUS,
  textInset: theme.spacing.s4,
  textColor: theme.colors.black,
  textSize: SHEET_INPUT_TEXT_SIZE,
  textLine: SHEET_INPUT_TEXT_LINE,
  textFont: fonts.semiBold,
  chipTextSize: 13,
  slotRadius: CODE_STEP_LOOK.slotRadius,
  slotGap: CODE_STEP_LOOK.slotGap,
  button: theme.colors.white,
};

// Email + one-time code. The code proves the address, the core API answers
// with a Firebase custom token, and signing in with it runs the same
// authenticated entry as every other method (user.auth), so a new account is
// a regular app sign-up minus the email verification step.
//
// The code step is the one Link in Bio draws (CodeStep): checked at the
// sixth digit, with the sheet staying up, green, until the fan is signed in.
const LoginEmailCodeSheetContent = ({
  closeModal,
  initialEmail,
  onLoginSuccess,
  onUsePassword,
}: LoginEmailSheetProps & {
  closeModal: () => void;
  onUsePassword: () => void;
}) => {
  const session = useData();
  const initialCleanEmail = sanitizeAuthInput(initialEmail ?? "");
  const emailState = useNativeState(initialCleanEmail);
  const [hasValidEmail, setHasValidEmail] = useState(
    isValidEmail(initialCleanEmail),
  );
  const [sending, setSending] = useState(false);
  const [challenge, setChallenge] = useState<{
    id: string;
    sentTo: string;
    resendAt: number;
  } | null>(null);
  const [status, setStatus] = useState<CodeStepStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const handleLoginSuccess = useCallback(async () => {
    await onLoginSuccess?.();
  }, [onLoginSuccess]);
  const { authenticate, isSubmitting, authStatus } = useLoginRegister(
    false,
    handleLoginSuccess,
  );
  const emailRef = useRef<LoginSheetTextInputRef>(null);
  const inputPlaceholderColor = hexToRGB(theme.colors.black, 0.42);
  const busy = sending || isSubmitting;
  const codeUp = challenge !== null;
  const arrival = useFieldBecomesCode(codeUp);

  // Back at the address (× on the chip): the keyboard follows. The code
  // step takes it on its own as it arrives.
  const wasCodeUp = useRef(false);
  useEffect(() => {
    if (wasCodeUp.current && !codeUp) emailRef.current?.focus();
    wasCodeUp.current = codeUp;
  }, [codeUp]);

  // The sheet stays up, verified, while the fan is signed in with the code,
  // and leaves once the app account has loaded. Firebase completing alone
  // does not mean user.auth has finished. The hook reports account failures.
  useEffect(() => {
    if (status !== "verified") return;
    if (authStatus === "logged-in" || authStatus === "db-user-error")
      closeModal();
  }, [authStatus, closeModal, status]);

  const start = useCallback(
    async (email: string) => {
      trackOnb("signup", "login", "email-code-requested");
      const result = await session.api.identity.emailCode.start.mutate({
        email,
      });
      setChallenge({
        id: result.challengeId,
        sentTo: email,
        resendAt: Date.now() + result.resendAfterSeconds * 1000,
      });
    },
    [session],
  );

  const sendCode = useCallback(async () => {
    setError(null);
    setSending(true);
    try {
      await start(sanitizeAuthInput(emailState.value).trim());
    } catch (error) {
      toast.error("We couldn't send a code", {
        description: "Check the address and try again.",
      });
      captureException(error, {
        channel: "error",
        area: "auth",
        operation: "email-code-start",
      });
    } finally {
      setSending(false);
    }
  }, [emailState, start]);

  const resendCode = useCallback(async () => {
    if (!challenge) return;
    setError(null);
    try {
      await start(challenge.sentTo);
    } catch (error) {
      setError("Something went wrong. Please try again.");
      captureException(error, {
        channel: "error",
        area: "auth",
        operation: "email-code-start",
      });
      throw error;
    }
  }, [challenge, start]);

  const verifyCode = useCallback(
    async (code: string) => {
      if (!challenge) return;
      setError(null);
      setStatus("checking");
      try {
        const { customToken } =
          await session.api.identity.emailCode.verify.mutate({
            challengeId: challenge.id,
            code,
          });
        setStatus("verified");
        await authenticate(
          undefined,
          undefined,
          undefined,
          undefined,
          customToken,
        );
      } catch (error) {
        setStatus("idle");
        const trpcCode = trpcErrorCode(error);
        if (trpcCode === "BAD_REQUEST") {
          setError("That code didn't match. Try again or request a new one.");
          return;
        }
        if (trpcCode === "CONFLICT") {
          setError(
            "This email is linked to another account. Use your existing login or contact support.",
          );
          return;
        }
        setError("Something went wrong. Please try again.");
        captureException(error, {
          channel: "error",
          area: "auth",
          operation: "email-code-verify",
        });
      }
    },
    [authenticate, challenge, session],
  );

  return (
    <BottomSheetTopBarLayout title="Sign up or Log in">
      <Animated.View style={[{ width: "100%" }, arrival.boxStyle]}>
        <View
          aria-hidden={codeUp}
          accessibilityElementsHidden={codeUp}
          importantForAccessibility={codeUp ? "no-hide-descendants" : "auto"}
          onLayout={arrival.onEmailPaneLayout}
          pointerEvents={codeUp ? "none" : "auto"}
          style={[
            { gap: theme.spacing.s4 },
            arrival.flow === "code" ? OVER_BOX : null,
          ]}
        >
          <Animated.View style={arrival.wordsStyle}>
            <BottomSheetSubtitle>
              Enter your email and we&apos;ll send you a six-digit code to sign
              in.
            </BottomSheetSubtitle>
          </Animated.View>
          <Animated.View
            onLayout={arrival.onFieldLayout}
            style={[styles.sheetInputFrame, arrival.fieldStyle]}
          >
            <LoginSheetTextInput
              placeholder="Email"
              testID="login-email-input"
              value={emailState}
              onChangeText={(text) =>
                setHasValidEmail(isValidEmail(sanitizeAuthInput(text)))
              }
              placeholderTextColor={inputPlaceholderColor}
              onSubmitEditing={() => {
                if (!hasValidEmail || busy || codeUp) return;
                return sendCode();
              }}
              autoCapitalize="none"
              autoCorrect={false}
              autoComplete="email"
              keyboardType="email-address"
              returnKeyType="done"
              autoFocus
              ref={emailRef}
              cursorColor={theme.colors.black}
              selectionColor={hexToRGB(theme.colors.black, 0.2)}
              hostStyle={styles.sheetInputHost}
              style={{ opacity: busy ? 0.5 : 1 }}
              textStyle={styles.sheetInputText}
            />
          </Animated.View>
          <Animated.View
            onLayout={arrival.onButtonLayout}
            style={arrival.fieldStyle}
          >
            <BottomSheetSubmitButton
              // Still sending until the field has gone into the code step.
              label={sending || codeUp ? "Sending code…" : "CONTINUE"}
              onPress={sendCode}
              enabled={hasValidEmail && !busy && !codeUp}
              applyOpacityWhenDisabled={!sending && !codeUp}
              testID="SignUpButton"
              accessibilityLabel={
                sending || codeUp ? "Sending code…" : "CONTINUE"
              }
              sentryEventName="login_continue"
            />
          </Animated.View>
          <Animated.View style={arrival.wordsStyle}>
            <LPressable
              sentryEventName="login_use_password"
              accessibilityLabel="Use password instead"
              testID="login-use-password"
              disabled={busy || codeUp}
              hitSlop={8}
              onPress={onUsePassword}
            >
              <LText
                color="blackTranslucent50"
                fontFamily={fonts.medium}
                fontSize={13}
                textAlign="center"
              >
                Use password instead
              </LText>
            </LPressable>
          </Animated.View>
        </View>

        {challenge ? (
          <View style={arrival.flow === "code" ? null : OVER_BOX}>
            <CodeStep
              email={challenge.sentTo}
              status={status}
              verifiedText="Verified · signing you in…"
              error={error}
              resendAt={challenge.resendAt}
              onCode={(code) => void verifyCode(code)}
              onInput={() => setError(null)}
              onResend={resendCode}
              onEdit={() => {
                setError(null);
                setChallenge(null);
              }}
              look={CODE_STEP_LOOK}
              ids="login-code"
              events={{
                changeEmail: "login_code_change_email",
                resend: "login_code_resend",
                paste: "login_code_paste",
              }}
              motion={arrival.motion}
              onGeometry={arrival.onGeometry}
            />
          </View>
        ) : null}

        {arrival.drawn && challenge ? (
          <FieldBecomesCode
            clock={arrival.clock}
            from={arrival.drawn.from}
            to={arrival.drawn.to}
            look={EMAIL_FIELD_LOOK}
            address={challenge.sentTo}
          />
        ) : null}
      </Animated.View>
    </BottomSheetTopBarLayout>
  );
};

const LoginEmailSheet = React.forwardRef<SheetAdapterRef, LoginEmailSheetProps>(
  function LoginEmailSheet(props, ref) {
    // Codes are the default; the password form stays for accounts that have
    // one and for anyone who prefers it.
    const [method, setMethod] = useState<"code" | "password">("code");
    return (
      <AppModalSheet ref={ref} keyboardBehavior="none" showHandle={false}>
        {({ closeModal }) => (
          <StableKeyboardInset>
            {method === "code" ? (
              <LoginEmailCodeSheetContent
                {...props}
                closeModal={closeModal}
                onUsePassword={() => {
                  trackOnb("signup", "login", "password-pressed");
                  setMethod("password");
                }}
              />
            ) : (
              <LoginEmailSheetContent {...props} closeModal={closeModal} />
            )}
          </StableKeyboardInset>
        )}
      </AppModalSheet>
    );
  },
);

const getLoginLayoutHeights = (
  viewportHeight: number,
  safeAreaHeight: number,
) => {
  const contentHeight = Math.max(0, viewportHeight - safeAreaHeight);
  const requiredLoginControlsHeight =
    LOGIN_TITLE_HEIGHT +
    LOGIN_TITLE_VERTICAL_PADDING +
    AUTH_BUTTON_HEIGHT +
    AUTH_SEPARATOR_HEIGHT +
    (Platform.OS === "ios" ? SOCIAL_ICON_BUTTON_SIZE : AUTH_BUTTON_HEIGHT) +
    AUTH_BUTTON_GAP * 2 +
    LEGAL_TEXT_GAP +
    LEGAL_TEXT_HEIGHT +
    LEGAL_TEXT_BOTTOM_PADDING;
  const maxCarouselHeight = contentHeight * 0.5;
  const minCarouselHeight = contentHeight * 0.35;
  const carouselHeight = clamp(
    contentHeight - TOP_BAR_HEIGHT - requiredLoginControlsHeight,
    minCarouselHeight,
    maxCarouselHeight,
  );

  return carouselHeight;
};

const SkeletonOverlay = () => {
  const window = useWindowDimensions();
  const { width: screenWidth } = getViewPort(window);
  const translateX = useSharedValue(-SHIMMER_WIDTH);
  React.useEffect(() => {
    translateX.set(
      withRepeat(
        withTiming(screenWidth, {
          duration: 1200,
          easing: Easing.inOut(Easing.ease),
        }),
        -1,
        false,
      ),
    );
  }, [screenWidth, translateX]);
  const shimmerStyle = useAnimatedStyle(() => ({
    position: "absolute" as const,
    top: 0,
    bottom: 0,
    width: SHIMMER_WIDTH,
    transform: [{ translateX: translateX.value }],
  }));
  return (
    <Animated.View
      style={[
        StyleSheet.absoluteFill,
        {
          borderRadius: theme.borderRadii["m-l"],
          overflow: "hidden",
        },
      ]}
      pointerEvents="none"
    >
      <Animated.View style={shimmerStyle}>
        <LinearGradient
          colors={["transparent", "rgba(255,255,255,0.25)", "transparent"]}
          start={{ x: 0, y: 0.5 }}
          end={{ x: 1, y: 0.5 }}
          style={StyleSheet.absoluteFill}
        />
      </Animated.View>
    </Animated.View>
  );
};

const Auth = (props?: {
  backgroundColor?: BackgroundColor;
  onLoginSuccess?: () => void;
  bottomSheet?: boolean;
  availableHeight?: number;
}) => {
  const bottomSheet = props?.bottomSheet ?? false;
  const insets = useSafeAreaInsets();
  const bottomSheetManager = useBottomSheetManager();
  const windowDimensions = useWindowDimensions();
  const { height: viewportHeight } = getViewPort(windowDimensions);
  const layoutHeight = props?.availableHeight ?? viewportHeight;
  const carouselHeight = getLoginLayoutHeights(
    layoutHeight,
    bottomSheet || Platform.OS === "web" ? 0 : insets.top + insets.bottom,
  );
  const { prefilledEmail } = useLocalSearchParams<AuthParams>();
  const { isInMiniApp, isLoading } = useFarcasterContext();
  const onLoginSuccess = props?.onLoginSuccess;

  const [showPreviewChannelSwitcher, setShowPreviewChannelSwitcher] =
    useState(false);

  const email = prefilledEmail ?? "";
  const [submittingMethod, setSubmittingMethod] = useState<
    "google" | "apple" | null
  >(null);

  const { isSubmitting, authenticate } = useLoginRegister();

  const openEmailLogin = useCallback(() => {
    trackOnb("signup", "login", "email-pressed");
    bottomSheetManager.open(
      <LoginEmailSheet initialEmail={email} onLoginSuccess={onLoginSuccess} />,
      { mode: "push" },
    );
  }, [bottomSheetManager, email, onLoginSuccess]);

  const buttonDimStyle = useAnimatedStyle(() => ({
    opacity: withTiming(isSubmitting ? 0.55 : 1, { duration: 300 }),
  }));
  const activeTextStyle = {
    flexDirection: "row" as const,
    gap: 8,
    alignItems: "center" as const,
  };

  const tracked = useRef(false);
  useEffect(() => {
    if (!tracked.current) {
      tracked.current = true;
      trackOnb("signup", "login", "view");
    }
  }, []);

  const canSwitchPreviewChannel = capabilities.allowed(
    "mobileApp.previewChannelSwitcher",
  );

  const content = (
    <>
      {/* Top bar: Logo + Help */}
      <LBox flexDirection="row" justifyContent="center" alignItems="center">
        <LBox flex={1} />
        <LBox entering={FadeInUp.duration(500)}>
          <LIcon name="lissen_logo_full" sizeHeight={40} sizeWidth={140} />
        </LBox>
        <LBox flex={1} alignItems="flex-end">
          <LBox entering={FadeInRight.duration(400).delay(1000)}>
            <LIcon
              colors={{ color: "#F6B452" }}
              sentryEventName="login_show_intercom"
              name="help"
              size={34}
              onPress={async () => {
                await presentIntercomSupport(
                  "present-intercom-from-login-help",
                );
              }}
            />
          </LBox>
        </LBox>
      </LBox>

      <LBox entering={FadeIn.duration(500)} style={{ height: carouselHeight }}>
        <LoginCarousel startDelay={800} />
      </LBox>

      {/* Bottom half: tagline + buttons + terms */}
      <LBox flex={1} justifyContent={bottomSheet ? "space-between" : undefined}>
        {/* Android: 3 equal sections inside the flex area */}
        <LBox flex={bottomSheet ? undefined : 1}>
          {/* Section 1: tagline */}
          <LBox
            entering={FadeInUp.duration(500).delay(300)}
            flex={Platform.OS === "android" ? 1 : undefined}
            justifyContent={Platform.OS === "android" ? "center" : undefined}
          >
            <LText
              variant="introTitle"
              textAlign="center"
              paddingVertical={Platform.OS === "android" ? undefined : "m2"}
            >
              Be that fan, finally
            </LText>
          </LBox>

          {/* Section 2: Auth buttons */}
          <LBox
            entering={FadeInUp.duration(500).delay(800)}
            flex={Platform.OS === "android" ? 1 : undefined}
            justifyContent={Platform.OS === "android" ? "center" : undefined}
            gap="s4"
            paddingHorizontal="l2"
          >
            {!isLoading && !isInMiniApp && (
              <>
                <Animated.View
                  style={[
                    { height: AUTH_BUTTON_HEIGHT, width: "100%" },
                    buttonDimStyle,
                  ]}
                >
                  <LPressable
                    sentryEventName="login_email_pressed"
                    accessibilityLabel="Continue with email"
                    testID="login-continue-with-email"
                    fadeEnabled
                    style={{
                      flex: 1,
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "white",
                      borderRadius: theme.borderRadii["m-l"],
                    }}
                    disabled={isSubmitting}
                    onPress={openEmailLogin}
                  >
                    <Animated.View style={activeTextStyle}>
                      <LIcon
                        colors={{ color: "black" }}
                        name="mail"
                        size={AUTH_BUTTON_ICON_SIZE}
                      />
                      <LText
                        color="black"
                        fontSize={AUTH_BUTTON_FONT_SIZE}
                        fontFamily={fonts.medium}
                        lineHeight={AUTH_BUTTON_LINE_HEIGHT}
                      >
                        Continue with email
                      </LText>
                    </Animated.View>
                  </LPressable>
                </Animated.View>

                <LText
                  color="whiteTranslucent60"
                  fontSize={16}
                  lineHeight={AUTH_SEPARATOR_HEIGHT}
                  textAlign="center"
                >
                  or
                </LText>

                <LBox
                  flexDirection={Platform.OS === "ios" ? "row" : undefined}
                  justifyContent="center"
                  gap="m1"
                >
                  {Platform.OS === "ios" && (
                    <Animated.View style={buttonDimStyle}>
                      <SocialButton
                        type="apple"
                        variant="icon"
                        onComplete={(...args) => {
                          setSubmittingMethod("apple");
                          trackOnb("signup", "login", "apple-pressed");
                          return authenticate(...args);
                        }}
                        disabled={isSubmitting}
                        hideContent={
                          isSubmitting && submittingMethod === "apple"
                        }
                      />
                      {isSubmitting && submittingMethod === "apple" && (
                        <SkeletonOverlay />
                      )}
                    </Animated.View>
                  )}
                  <Animated.View
                    style={[
                      buttonDimStyle,
                      Platform.OS === "android" && { width: "100%" },
                    ]}
                  >
                    <SocialButton
                      type="google"
                      variant={Platform.OS === "ios" ? "icon" : "outline"}
                      onComplete={(...args) => {
                        setSubmittingMethod("google");
                        trackOnb("signup", "login", "google-pressed");
                        return authenticate(...args);
                      }}
                      disabled={isSubmitting}
                      hideContent={
                        isSubmitting && submittingMethod === "google"
                      }
                    />
                    {isSubmitting && submittingMethod === "google" && (
                      <SkeletonOverlay />
                    )}
                  </Animated.View>
                </LBox>
              </>
            )}
          </LBox>
          {/* Section 3: empty spacer */}
          {Platform.OS === "android" && <LBox flex={1} />}
        </LBox>

        {/* Terms - outside the red box, pinned at bottom */}
        <LBox entering={FadeInDown.duration(400).delay(1000)} pb="m1">
          <LText paddingHorizontal="s4" textAlign="center" variant="bodyM">
            By continuing you indicate that you have read and agree to
            Lissen&apos;s{" "}
            <LText
              sentryEventName="login_terms"
              onPress={() => WebBrowser.openBrowserAsync(config.TERMS_URL)}
              variant="bodyM"
              textDecorationLine="underline"
              color="linkButtonText"
            >
              Terms of Service
            </LText>{" "}
            and{" "}
            <LText
              sentryEventName="login_privacy"
              onPress={() => WebBrowser.openBrowserAsync(config.PRIVACY_URL)}
              variant="bodyM"
              textDecorationLine="underline"
              color="linkButtonText"
            >
              Privacy Policy
            </LText>
            .
          </LText>
        </LBox>
      </LBox>

      {canSwitchPreviewChannel ? (
        <LPressable
          sentryEventName="login_preview_channel_open"
          accessibilityLabel="Switch preview channel"
          testID="login-preview-channel-open"
          fadeEnabled
          disabled={isSubmitting}
          hitSlop={8}
          onPress={() => setShowPreviewChannelSwitcher(true)}
          style={{
            alignItems: "center",
            backgroundColor: theme.colors.blackTranslucent60,
            borderColor: theme.colors.whiteTranslucent20,
            borderWidth: 1,
            borderRadius: theme.borderRadii.max,
            height: 44,
            justifyContent: "center",
            left: insets.left + theme.spacing.s4,
            position: "absolute",
            top: insets.top + theme.spacing.s4,
            width: 44,
            zIndex: 10,
          }}
        >
          <LText color="white" fontFamily={fonts.medium} fontSize={12}>
            DEV
          </LText>
        </LPressable>
      ) : null}
      {canSwitchPreviewChannel ? (
        <EmailModal
          visible={showPreviewChannelSwitcher}
          onClose={() => setShowPreviewChannelSwitcher(false)}
        >
          <PreviewChannelSwitcher
            visible={showPreviewChannelSwitcher}
            onClose={() => setShowPreviewChannelSwitcher(false)}
          />
        </EmailModal>
      ) : null}
    </>
  );

  if (bottomSheet) {
    return (
      <LBox flex={1} paddingHorizontal="s4" paddingTop="m1">
        {content}
      </LBox>
    );
  }

  return (
    <LScreen safeArea safeAreaEdges={["top", "bottom"]}>
      {content}
    </LScreen>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.7)",
    padding: 24,
  },
  modalKeyboardContainer: {
    width: "100%",
    maxWidth: 400,
  },
  modalContent: {
    width: "100%",
    maxWidth: 400,
    backgroundColor: theme.colors.screenBg,
    borderRadius: theme.borderRadii.l,
    padding: 24,
    borderWidth: 1,
    borderColor: theme.colors.greyBorder,
  },
  sheetInputFrame: {
    backgroundColor: SHEET_FIELD_FILL,
    borderColor: SHEET_FIELD_BORDER,
    borderRadius: SHEET_FIELD_RADIUS,
    borderWidth: 1,
    height: 58,
    paddingHorizontal: theme.spacing.s4,
    justifyContent: "center",
    width: "100%",
  },
  sheetInputHost: {
    flex: 1,
    width: "100%",
  },
  sheetInputText: {
    color: theme.colors.black,
    fontFamily: fonts.semiBold,
    fontSize: SHEET_INPUT_TEXT_SIZE,
    fontWeight: "600",
    lineHeight: SHEET_INPUT_TEXT_LINE,
  },
});

export default Auth;
